'use client';

import type { ICartItem } from '@/api/types/cart';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { useCallback } from 'react';

// 扩展CartItem类型以支持更多字段
interface CartItem extends ICartItem {
  product: ICartItem['product'] & {
    price_info?: any;
    color?: string;
  };
}

export function useCartGaTracking() {
  const { trackEvent } = useReporting();

  /**
   * GA埋点：数量变更
   * 对应Vue版本中CartItem.vue的qty事件
   */
  const trackQuantityChange = useCallback(() => {
    try {
      trackEvent(
        {
          commandType: 'event',
          eventName: 'qty',
          properties: {
            event_label: 'Qty',
            event_category: 'ADD_CART',
          },
        },
        [ProviderType.GA4],
      );

      logger.log('🔍 GA埋点 - qty:');
    } catch (error) {
      logger.error('GA qty tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * GA埋点：购物车商品集合
   * 对应Vue版本中CartRight.vue的add_cart事件
   * 在购物车价格计算完成后触发，用于追踪购物车中的商品集合
   */
  const trackAddCart = useCallback(
    (selectedItems: CartItem[]) => {
      try {
        if (!selectedItems || selectedItems.length === 0) {
          return;
        }

        // 构建商品数组，每个商品包含item_id
        const items = selectedItems.map((item) => ({
          item_id: item.product?.shop_sku || '',
        }));

        // 构建sku字符串，所有商品的shop_sku用逗号拼接
        const skuString = selectedItems.map((item) => item.product?.shop_sku || '').join(',');

        trackEvent(
          {
            commandType: 'event',
            eventName: 'add_cart',
            properties: {
              items,
              event_label: 'Addcart_product',
              event_category: 'ADD_CART',
              sku: skuString,
            },
          },
          [ProviderType.GA4],
        );

        logger.log('🔍 GA埋点 - add_cart:', { selectedItems, items, sku: skuString });
      } catch (error) {
        logger.error('GA add_cart tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * GA埋点：转化事件
   * 对应Vue版本中的conversion事件
   */
  const trackConversion = useCallback(
    (value?: string, conversionCurrency?: string) => {
      try {
        const properties: any = {
          send_to: `${env.NEXT_PUBLIC_GA4_AD_ID2}/${env.NEXT_PUBLIC_GA4_ADD_TO_CART}`,
        };

        // 如果有价值和币种，添加到属性中
        if (value && parseFloat(value) > 0) {
          properties.value = value;
        }
        if (conversionCurrency) {
          properties.currency = conversionCurrency;
        }

        trackEvent(
          {
            commandType: 'event',
            eventName: 'conversion',
            properties,
          },
          [ProviderType.GA4],
        );

        logger.log('🔍 GA埋点 - conversion:', { value, currency: conversionCurrency });
      } catch (error) {
        logger.error('GA conversion tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * GA埋点：继续购物
   * 对应Vue版本中CartRight.vue的continue_shopping事件
   */
  const trackContinueShopping = useCallback(() => {
    try {
      trackEvent(
        {
          commandType: 'event',
          eventName: 'continue_shopping',
          properties: {
            event_label: 'Continue_shopping',
            event_category: 'ADD_CART',
          },
        },
        [ProviderType.GA4],
      );

      logger.log('🔍 GA埋点 - continue_shopping');
    } catch (error) {
      logger.error('GA continue_shopping tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * GA埋点：购物车编辑
   * 对应Vue版本中CartItem.vue的cart_edit事件
   */
  const trackCartEdit = useCallback(() => {
    try {
      trackEvent(
        {
          commandType: 'event',
          eventName: 'cart_edit',
          properties: {
            event_label: 'Edit',
            event_category: 'ADD_CART',
          },
        },
        [ProviderType.GA4],
      );

      logger.log('🔍 GA埋点 - cart_edit');
    } catch (error) {
      logger.error('GA cart_edit tracking error:', error);
    }
  }, [trackEvent]);

  return {
    trackQuantityChange,
    trackConversion,
    trackAddCart,
    trackContinueShopping,
    trackCartEdit,
  };
}
